import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  OutlinedInput,
  Chip,
  TextField,
  ListSubheader,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ButtonBase,
  Alert,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import CloseIcon from '@mui/icons-material/Close';
import useIsMobile from '../hooks/useIsMobile';
import { serverFileName } from './fileDownload';

// School year that is running now: Sep–Aug, keyed by its first calendar year.
const currentSchoolYear = () => {
  const d = new Date();
  return d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1;
};
const monthKey = (d) =>
  `m:${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const ALL_SECTIONS = {
  latestProfiles: true,
  progressOverTime: true,
  attendance: true,
  groupComparison: true,
  aiRecommendations: true,
  teacherComment: true,
};

// «Скачать отчёты»: who → which report → period → download. Everything else
// (programs, sections, comments, AI, wording) sits under «Дополнительно».
// Self-contained: fetches its own students + groups so it works from any page.
// Props: open, onClose, language ('en'|'ru'), initialStudentId?,
// initialStudentIds? (several children), defaultMode? ('combined'|'group')
export default function PdfExportPanel({
  open,
  onClose,
  language = 'ru',
  initialStudentId = '',
  initialStudentIds,
  defaultMode = 'combined',
}) {
  const isRu = language !== 'en';
  const L = (en, ru) => (isRu ? ru : en);
  const isMobile = useIsMobile();

  const [lang, setLang] = useState(language === 'en' ? 'en' : 'ru');
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Who: one student, several students, or a whole group
  const [who, setWho] = useState('student');
  const [studentId, setStudentId] = useState(initialStudentId);
  const [multiIds, setMultiIds] = useState([]);
  const [combGroupId, setCombGroupId] = useState('');
  // Which report: 'parent' | 'full' | 'groupAnalytics'
  const [report, setReport] = useState('parent');
  const mode = who === 'group' ? 'group' : 'student';

  // Export scope: which assessments (templates) and which period. Period keys:
  // 's:YYYY' school year, 'y:YYYY' calendar year, 'm:YYYY-MM' month, '' = all time.
  const [allTemplates, setAllTemplates] = useState([]);
  const [studentTemplates, setStudentTemplates] = useState(null);
  const [templateIds, setTemplateIds] = useState([]);
  const [reportPeriod, setReportPeriod] = useState(
    () => `s:${currentSchoolYear()}`
  );
  const [otherPeriod, setOtherPeriod] = useState(false);
  const [sections, setSections] = useState(ALL_SECTIONS);

  // AI teacher comments: { done, total, name } while running.
  const [aiBusy, setAiBusy] = useState(null);
  const [aiError, setAiError] = useState('');
  const [aiFresh, setAiFresh] = useState(false);

  // Several students / whole group: one PDF or a ZIP with a file per child
  const [groupOutput, setGroupOutput] = useState('merged');
  const [bundle, setBundle] = useState({
    compact: true,
    cover: true,
    roster: false,
  });

  // "Not yet learned" reframing of very-low topics (< threshold% mastery).
  const [notYet, setNotYet] = useState({
    enabled: false,
    exclude: false,
    showAll: false,
    threshold: 20,
  });
  const [notice, setNotice] = useState({ level: 'none', text: '' });
  const [comment, setComment] = useState('');
  const [perStudentComments, setPerStudentComments] = useState({});
  const [showPerStudent, setShowPerStudent] = useState(false);
  const [parentMode, setParentMode] = useState(false);

  const applyParentPreset = useCallback(() => {
    setSections({
      latestProfiles: true,
      progressOverTime: false,
      attendance: true,
      groupComparison: false,
      aiRecommendations: true,
      teacherComment: true,
    });
    setNotYet({ enabled: true, exclude: false, showAll: true, threshold: 20 });
    setBundle({ compact: true, cover: true, roster: false });
    setNotice({
      level: 'green',
      text: L(
        'This snapshot shows what your child has learned and the next steps. Every child progresses at their own pace — we celebrate the journey.',
        'Этот снимок показывает, что освоил ваш ребёнок, и следующие шаги. Каждый ребёнок развивается в своём темпе — мы радуемся каждому шагу вперёд.'
      ),
    });
    setParentMode(true);
  }, [L]);

  const applyFull = () => {
    setSections(ALL_SECTIONS);
    setNotYet({
      enabled: false,
      exclude: false,
      showAll: false,
      threshold: 20,
    });
    setNotice({ level: 'none', text: '' });
    setParentMode(false);
  };

  const pickReport = (r) => {
    setReport(r);
    if (r === 'parent') applyParentPreset();
    if (r === 'full') applyFull();
    if (r === 'groupAnalytics') setWho('group');
  };

  // Group analytics state
  const [groupIds, setGroupIds] = useState([]);
  const [gaSections, setGaSections] = useState({
    masteryTrends: true,
    studentImprovement: true,
    struggledItems: true,
    groupComparison: true,
  });

  // Every time the dialog opens: who it was opened for, parent report by default
  const idsKey = (initialStudentIds || []).join(',');
  useEffect(() => {
    if (!open) return;
    setError('');
    const ids = initialStudentIds || [];
    if (ids.length > 1) {
      setWho('several');
      setMultiIds(ids);
      setReport('parent');
      applyParentPreset();
    } else if (ids.length === 1 || initialStudentId) {
      setWho('student');
      setStudentId(ids[0] || initialStudentId);
      setReport('parent');
      applyParentPreset();
    } else if (defaultMode === 'group') {
      setWho('group');
      setReport('groupAnalytics');
    } else {
      setReport('parent');
      applyParentPreset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idsKey, initialStudentId, defaultMode]);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      fetch('/api/students', { credentials: 'include' })
        .then((r) => r.json())
        .catch(() => []),
      fetch('/api/analytics/groups', { credentials: 'include' })
        .then((r) => r.json())
        .catch(() => []),
      fetch('/api/templates', { credentials: 'include' })
        .then((r) => r.json())
        .catch(() => []),
    ]).then(([studs, grps, tpls]) => {
      setStudents(
        (Array.isArray(studs) ? studs : [])
          .map((s) => ({ _id: s._id, name: s.name }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setGroups(Array.isArray(grps) ? grps : []);
      setAllTemplates(
        (Array.isArray(tpls) ? tpls : []).map((t) => ({
          _id: t._id,
          name: t.name,
        }))
      );
    });
  }, [open]);

  // Templates this student was actually assessed on (all versions)
  useEffect(() => {
    if (!open || who !== 'student' || !studentId) {
      setStudentTemplates(null);
      return undefined;
    }
    let cancelled = false;
    fetch(`/api/profiles/student/${studentId}?all=true&active=1`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((profiles) => {
        if (cancelled) return;
        const seen = new Map();
        (Array.isArray(profiles) ? profiles : []).forEach((p) => {
          const t = p.templateId;
          if (t && t._id && !seen.has(t._id))
            seen.set(t._id, { _id: t._id, name: t.name });
        });
        setStudentTemplates([...seen.values()]);
      })
      .catch(() => !cancelled && setStudentTemplates([]));
    return () => {
      cancelled = true;
    };
  }, [open, who, studentId]);

  const templateOptions =
    who === 'student' && studentTemplates ? studentTemplates : allTemplates;
  useEffect(() => {
    setTemplateIds((ids) => {
      const next = ids.filter((id) =>
        templateOptions.some((t) => t._id === id)
      );
      return next.length === ids.length ? ids : next;
    });
  }, [templateOptions]);
  useEffect(() => {
    setLang(language === 'en' ? 'en' : 'ru');
  }, [language]);

  // ── Period choices ──
  const monthNames = isRu
    ? [
        'Январь',
        'Февраль',
        'Март',
        'Апрель',
        'Май',
        'Июнь',
        'Июль',
        'Август',
        'Сентябрь',
        'Октябрь',
        'Ноябрь',
        'Декабрь',
      ]
    : [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
  const monthOptions = useMemo(() => {
    const out = [];
    const d = new Date();
    d.setDate(1);
    for (let i = 0; i < 24; i++) {
      out.push({
        value: monthKey(d),
        label: `${monthNames[d.getMonth()]} ${d.getFullYear()}`,
      });
      d.setMonth(d.getMonth() - 1);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRu]);
  const sy = currentSchoolYear();
  const schoolYearOptions = [sy, sy - 1, sy - 2].map((y) => ({
    value: `s:${y}`,
    label: L(`School year ${y}–${y + 1}`, `${y}–${y + 1} учебный год`),
  }));
  const thisYear = new Date().getFullYear();
  const yearOptions = [thisYear, thisYear - 1, thisYear - 2].map((y) => ({
    value: `y:${y}`,
    label: L(`Year ${y}`, `${y} год`),
  }));
  const lastMonth = (() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return monthKey(d);
  })();
  const quickPeriods = [
    { value: `s:${sy}`, label: L('This school year', 'Этот учебный год') },
    { value: monthKey(new Date()), label: L('This month', 'Этот месяц') },
    { value: lastMonth, label: L('Last month', 'Прошлый месяц') },
    { value: '', label: L('All time', 'Всё время') },
  ];
  const periodLabel = (v) =>
    [
      ...quickPeriods,
      ...schoolYearOptions,
      ...yearOptions,
      ...monthOptions,
    ].find((o) => o.value === v)?.label || v;
  const isQuick =
    !otherPeriod && quickPeriods.some((p) => p.value === reportPeriod);

  // ── Download helpers ──
  const downloadPdf = useCallback(
    async (endpoint, body, filename) => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setError(err.message || `PDF error: ${res.status}`);
          return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = serverFileName(res, filename);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        onClose && onClose();
      } catch (e) {
        setError(L('Failed to generate PDF', 'Не удалось создать PDF'));
      } finally {
        setLoading(false);
      }
    },
    [onClose, L]
  );

  // Latest AI insights for a student, generated when missing / fresh.
  const loadInsights = useCallback(
    async (sid, fresh) => {
      if (!fresh) {
        const r = await fetch(
          `/api/ai/insights/student/${sid}?language=${lang}`,
          { credentials: 'include' }
        );
        if (r.ok) return (await r.json()).insights;
        if (r.status !== 404) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.message || `HTTP ${r.status}`);
        }
      }
      const r = await fetch(`/api/ai/insights/student/${sid}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language: lang }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || `HTTP ${r.status}`);
      return data.insights;
    },
    [lang]
  );

  // Children the report is about (for AI comments and per-child notes)
  const targetStudents = useMemo(() => {
    if (who === 'student') return students.filter((s) => s._id === studentId);
    if (who === 'several')
      return multiIds.map(
        (id) => students.find((s) => s._id === id) || { _id: id, name: '…' }
      );
    const grp = groups.find((g) => g._id === combGroupId);
    return (grp && Array.isArray(grp.studentIds) ? grp.studentIds : [])
      .map((id) => students.find((s) => s._id === String(id)))
      .filter(Boolean);
  }, [who, students, studentId, multiIds, groups, combGroupId]);

  const fillAiComments = async () => {
    setAiError('');
    if (!targetStudents.length)
      return setAiError(
        L('Choose who the report is for', 'Выберите, для кого отчёт')
      );
    if (who === 'student') {
      const s = targetStudents[0];
      setAiBusy({ done: 0, total: 1, name: s.name });
      try {
        const ins = await loadInsights(s._id, aiFresh);
        if (ins && ins.teacherComment) setComment(ins.teacherComment);
      } catch (e) {
        setAiError(e.message);
      } finally {
        setAiBusy(null);
      }
      return undefined;
    }
    setShowPerStudent(true);
    const failed = [];
    for (let i = 0; i < targetStudents.length; i++) {
      const s = targetStudents[i];
      setAiBusy({ done: i, total: targetStudents.length, name: s.name });
      try {
        const ins = await loadInsights(s._id, aiFresh);
        if (ins && ins.teacherComment)
          setPerStudentComments((prev) => ({
            ...prev,
            [s._id]: ins.teacherComment,
          }));
      } catch (e) {
        failed.push(`${s.name}: ${e.message}`);
      }
    }
    setAiBusy(null);
    if (failed.length) setAiError(failed.join('\n'));
    return undefined;
  };

  const buildComments = () => {
    const out = {};
    Object.entries(perStudentComments).forEach(([id, v]) => {
      if (v && v.trim()) out[id] = v;
    });
    return out;
  };

  const many = who !== 'student';
  const ready =
    report === 'groupAnalytics'
      ? groupIds.length > 0
      : who === 'student'
        ? Boolean(studentId)
        : who === 'several'
          ? multiIds.length > 0
          : Boolean(combGroupId);

  const runCombined = () => {
    const commentFields = sections.teacherComment
      ? { comment, comments: buildComments() }
      : {};
    const common = {
      lang,
      sections,
      parentMode,
      notYetLearned: notYet.enabled,
      notYetExclude: notYet.exclude,
      notYetShowAll: notYet.showAll,
      notYetThreshold: notYet.threshold,
      notice:
        notice.level === 'none'
          ? null
          : { level: notice.level, text: notice.text },
      templateIds,
      period: reportPeriod || null,
      ...commentFields,
    };
    const ids =
      who === 'student' ? [studentId] : who === 'several' ? multiIds : [];
    const groupId = who === 'group' ? combGroupId : null;
    if (many && groupOutput === 'zip') {
      const grp = groups.find((g) => g._id === combGroupId);
      return downloadPdf(
        '/api/pdf/combined-report-zip',
        {
          ...common,
          studentIds: ids,
          groupId,
          compact: bundle.compact,
          cover: bundle.cover,
          roster: bundle.roster,
        },
        `reports_${(who === 'group' && grp && grp.name) || 'students'}.zip`
      );
    }
    const one =
      who === 'student' && targetStudents[0] ? targetStudents[0].name : null;
    return downloadPdf(
      '/api/pdf/combined-report',
      { ...common, studentIds: ids, groupId },
      one ? `report_${one}.pdf` : 'reports.pdf'
    );
  };

  const runGroupAnalytics = () =>
    downloadPdf(
      '/api/pdf/group-analytics',
      { lang, groupIds, sections: gaSections },
      'group_analytics.pdf'
    );

  const sectionCheck = (state, setState, key, label) => (
    <FormControlLabel
      key={key}
      control={
        <Checkbox
          size="small"
          checked={state[key]}
          onChange={(e) => setState({ ...state, [key]: e.target.checked })}
        />
      }
      label={<Typography variant="body2">{label}</Typography>}
    />
  );

  // ── Summary line under the choices ──
  const whoText =
    report === 'groupAnalytics'
      ? groupIds
          .map((id) => (groups.find((g) => g._id === id) || {}).name)
          .filter(Boolean)
          .join(', ')
      : who === 'student'
        ? (targetStudents[0] || {}).name
        : who === 'several'
          ? L(`${multiIds.length} children`, `детей: ${multiIds.length}`)
          : (groups.find((g) => g._id === combGroupId) || {}).name;
  const files =
    report === 'groupAnalytics'
      ? L('1 PDF', '1 PDF')
      : many && groupOutput === 'zip'
        ? L('ZIP — a PDF per child', 'ZIP — PDF на каждого')
        : L('1 PDF', '1 PDF');
  const reportName = {
    parent: L('for parents', 'для родителей'),
    full: L('detailed', 'подробный'),
    groupAnalytics: L('group analytics', 'аналитика группы'),
  }[report];

  const reportCard = ({ value, icon, title, text, disabled }) => {
    const on = report === value;
    return (
      <ButtonBase
        key={value}
        disabled={disabled}
        onClick={() => pickReport(value)}
        sx={{
          flex: '1 1 150px',
          display: 'block',
          textAlign: 'left',
          p: 1.5,
          borderRadius: '14px',
          border: '2px solid',
          borderColor: on ? 'primary.main' : 'divider',
          bgcolor: (t) =>
            on ? alpha(t.palette.primary.main, 0.06) : 'transparent',
          opacity: disabled ? 0.45 : 1,
        }}
      >
        <Box sx={{ color: on ? 'primary.main' : 'text.secondary', mb: 0.5 }}>
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
          {title}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', lineHeight: 1.35 }}
        >
          {text}
        </Typography>
      </ButtonBase>
    );
  };

  const stepTitle = (n, text) => (
    <Typography sx={{ fontWeight: 700, mb: 1, mt: n > 1 ? 2.5 : 0 }}>
      <Box
        component="span"
        sx={{
          display: 'inline-grid',
          placeItems: 'center',
          width: 22,
          height: 22,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: '#fff',
          fontSize: 12,
          mr: 1,
          verticalAlign: '1px',
        }}
      >
        {n}
      </Box>
      {text}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1.5 }}
      >
        <Box sx={{ flex: 1 }}>{L('Download reports', 'Скачать отчёты')}</Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={lang}
          onChange={(e, v) => v && setLang(v)}
        >
          <ToggleButton value="ru">RU</ToggleButton>
          <ToggleButton value="en">EN</ToggleButton>
        </ToggleButtonGroup>
        <IconButton onClick={onClose} aria-label={L('Close', 'Закрыть')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* 1 · Which report */}
        {stepTitle(1, L('Which report', 'Какой отчёт'))}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {reportCard({
            value: 'parent',
            icon: <FamilyRestroomIcon />,
            title: L('For parents', 'Для родителей'),
            text: L(
              'What the child learned and next steps, warm and short',
              'Что ребёнок освоил и что дальше — тепло и коротко'
            ),
          })}
          {reportCard({
            value: 'full',
            icon: <ArticleOutlinedIcon />,
            title: L('Detailed', 'Подробный'),
            text: L(
              'Everything: progress over time, attendance, group comparison',
              'Всё: рост со временем, посещаемость, сравнение с группой'
            ),
          })}
          {reportCard({
            value: 'groupAnalytics',
            icon: <InsightsIcon />,
            title: L('Group analytics', 'Аналитика группы'),
            text: L(
              'For the teacher: how one or more groups are doing',
              'Для учителя: как идут дела у групп'
            ),
            disabled: !groups.length,
          })}
        </Box>

        {/* 2 · Who */}
        {stepTitle(2, L('For whom', 'Для кого'))}
        {report === 'groupAnalytics' ? (
          <FormControl fullWidth size="small">
            <InputLabel>{L('Groups', 'Группы')}</InputLabel>
            <Select
              multiple
              value={groupIds}
              onChange={(e) =>
                setGroupIds(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value
                )
              }
              input={<OutlinedInput label={L('Groups', 'Группы')} />}
              renderValue={(sel) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {sel.map((id) => (
                    <Chip
                      key={id}
                      size="small"
                      label={
                        (groups.find((x) => x._id === id) || {}).name || id
                      }
                    />
                  ))}
                </Box>
              )}
            >
              {groups.map((g) => (
                <MenuItem key={g._id} value={g._id}>
                  <Checkbox size="small" checked={groupIds.includes(g._id)} />
                  {g.name}
                  {g.mine === false ? ` · ${g.teacher}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <>
            <ToggleButtonGroup
              size="small"
              exclusive
              fullWidth
              value={who}
              onChange={(e, v) => v && setWho(v)}
              sx={{ mb: 1.25 }}
            >
              <ToggleButton value="student">
                {L('One child', 'Один ребёнок')}
              </ToggleButton>
              <ToggleButton value="several">
                {L('Several', 'Несколько')}
              </ToggleButton>
              <ToggleButton value="group" disabled={!groups.length}>
                {L('Group', 'Группа')}
              </ToggleButton>
            </ToggleButtonGroup>
            {who === 'student' && (
              <Autocomplete
                options={students}
                value={students.find((s) => s._id === studentId) || null}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                onChange={(e, v) => setStudentId(v ? v._id : '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={L('Child', 'Ребёнок')}
                    placeholder={L('Type a name…', 'Начните вводить имя…')}
                  />
                )}
              />
            )}
            {who === 'several' && (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={students}
                value={multiIds.map(
                  (id) =>
                    students.find((s) => s._id === id) || { _id: id, name: '…' }
                )}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                onChange={(e, v) => setMultiIds(v.map((s) => s._id))}
                ChipProps={{ size: 'small' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={L('Children', 'Дети')}
                    placeholder={L('Add a child…', 'Добавить ребёнка…')}
                  />
                )}
              />
            )}
            {who === 'group' && (
              <FormControl fullWidth size="small">
                <InputLabel>{L('Group', 'Группа')}</InputLabel>
                <Select
                  value={combGroupId}
                  label={L('Group', 'Группа')}
                  onChange={(e) => setCombGroupId(e.target.value)}
                >
                  {groups.map((g) => (
                    <MenuItem key={g._id} value={g._id}>
                      {g.name}
                      {g.mine === false ? ` · ${g.teacher}` : ''} ·{' '}
                      {(g.studentIds || []).length}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {many && (
              <RadioGroup
                row
                value={groupOutput}
                onChange={(e) => setGroupOutput(e.target.value)}
                sx={{ mt: 0.75 }}
              >
                <FormControlLabel
                  value="merged"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      {L('One PDF', 'Один PDF')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="zip"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      {L('ZIP: a file per child', 'ZIP: файл на каждого')}
                    </Typography>
                  }
                />
              </RadioGroup>
            )}
          </>
        )}

        {/* 3 · Period */}
        {report !== 'groupAnalytics' && (
          <>
            {stepTitle(3, L('Period', 'Период'))}
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {quickPeriods.map((p) => (
                <Chip
                  key={p.value || 'all'}
                  label={p.label}
                  color={
                    isQuick && reportPeriod === p.value ? 'primary' : 'default'
                  }
                  variant={
                    isQuick && reportPeriod === p.value ? 'filled' : 'outlined'
                  }
                  onClick={() => {
                    setOtherPeriod(false);
                    setReportPeriod(p.value);
                  }}
                />
              ))}
              <Chip
                label={
                  isQuick ? L('Other…', 'Другой…') : periodLabel(reportPeriod)
                }
                color={!isQuick ? 'primary' : 'default'}
                variant={!isQuick ? 'filled' : 'outlined'}
                onClick={() => setOtherPeriod(true)}
              />
            </Box>
            {otherPeriod && (
              <Select
                fullWidth
                size="small"
                value={reportPeriod}
                onChange={(e) => setReportPeriod(e.target.value)}
                sx={{ mt: 1 }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
              >
                <MenuItem value="">{L('All time', 'Всё время')}</MenuItem>
                <ListSubheader>{L('School year', 'Учебный год')}</ListSubheader>
                {schoolYearOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
                <ListSubheader>
                  {L('Calendar year', 'Календарный год')}
                </ListSubheader>
                {yearOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
                <ListSubheader>{L('Month', 'Месяц')}</ListSubheader>
                {monthOptions.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          </>
        )}

        {/* Everything else */}
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            mt: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>
                {L('More options', 'Дополнительно')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {report === 'groupAnalytics'
                  ? L('Sections', 'Разделы отчёта')
                  : L(
                      'Programs, sections, teacher comment and AI, wording',
                      'Программы, разделы, комментарий учителя и AI, формулировки'
                    )}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {report === 'groupAnalytics' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {sectionCheck(
                  gaSections,
                  setGaSections,
                  'masteryTrends',
                  L('Mastery trends', 'Динамика освоения')
                )}
                {sectionCheck(
                  gaSections,
                  setGaSections,
                  'studentImprovement',
                  L('Student improvement', 'Прогресс учеников')
                )}
                {sectionCheck(
                  gaSections,
                  setGaSections,
                  'struggledItems',
                  L('Struggled items', 'Сложные темы')
                )}
                {sectionCheck(
                  gaSections,
                  setGaSections,
                  'groupComparison',
                  L('Group comparison', 'Сравнение групп')
                )}
              </Box>
            ) : (
              <>
                <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                  <InputLabel shrink>{L('Programs', 'Программы')}</InputLabel>
                  <Select
                    multiple
                    displayEmpty
                    value={templateIds}
                    onChange={(e) =>
                      setTemplateIds(
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : e.target.value
                      )
                    }
                    input={
                      <OutlinedInput
                        notched
                        label={L('Programs', 'Программы')}
                      />
                    }
                    renderValue={(sel) =>
                      sel.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {L('All', 'Все')}
                        </Typography>
                      ) : (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {sel.map((id) => (
                            <Chip
                              key={id}
                              size="small"
                              label={
                                (
                                  templateOptions.find((t) => t._id === id) ||
                                  {}
                                ).name || id
                              }
                            />
                          ))}
                        </Box>
                      )
                    }
                  >
                    {templateOptions.length === 0 && (
                      <MenuItem disabled>
                        {who === 'student' && studentTemplates
                          ? L('No assessments yet', 'Оценок пока нет')
                          : L('No programs', 'Нет программ')}
                      </MenuItem>
                    )}
                    {templateOptions.map((t) => (
                      <MenuItem key={t._id} value={t._id}>
                        <Checkbox
                          size="small"
                          checked={templateIds.includes(t._id)}
                        />
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  {L('Sections', 'Разделы')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  }}
                >
                  {sectionCheck(
                    sections,
                    setSections,
                    'latestProfiles',
                    L('Current results', 'Текущие результаты')
                  )}
                  {sectionCheck(
                    sections,
                    setSections,
                    'progressOverTime',
                    L('Progress over time', 'Рост со временем')
                  )}
                  {sectionCheck(
                    sections,
                    setSections,
                    'attendance',
                    L('Attendance', 'Посещаемость')
                  )}
                  {sectionCheck(
                    sections,
                    setSections,
                    'groupComparison',
                    L('Compare with group', 'Сравнение с группой')
                  )}
                  {sectionCheck(
                    sections,
                    setSections,
                    'aiRecommendations',
                    L('AI: where to improve', 'AI: где улучшить')
                  )}
                  {sectionCheck(
                    sections,
                    setSections,
                    'teacherComment',
                    L('Teacher comment', 'Комментарий учителя')
                  )}
                </Box>

                {sections.teacherComment && (
                  <Box sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 0.75,
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={Boolean(aiBusy) || !targetStudents.length}
                        onClick={fillAiComments}
                        startIcon={
                          aiBusy ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : null
                        }
                      >
                        {many
                          ? L(
                              '✨ AI comments for everyone',
                              '✨ AI-комментарии для всех'
                            )
                          : L('✨ Write with AI', '✨ Написать с AI')}
                      </Button>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={aiFresh}
                            onChange={(e) => setAiFresh(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="caption">
                            {L(
                              'fresh analysis (slower)',
                              'новый анализ (дольше)'
                            )}
                          </Typography>
                        }
                      />
                      {aiBusy && (
                        <Typography variant="caption" color="text.secondary">
                          {aiBusy.done + 1}/{aiBusy.total}
                          {aiBusy.name ? ` — ${aiBusy.name}` : ''}
                        </Typography>
                      )}
                    </Box>
                    {aiError && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{
                          display: 'block',
                          whiteSpace: 'pre-line',
                          mb: 0.5,
                        }}
                      >
                        {aiError}
                      </Typography>
                    )}
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      label={
                        many
                          ? L('Comment for everyone', 'Комментарий для всех')
                          : L('Teacher comment', 'Комментарий учителя')
                      }
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder={L(
                        'E.g. <b>Great progress!</b>',
                        'Напр. <b>Отличный прогресс!</b>'
                      )}
                      inputProps={{ maxLength: 2000 }}
                      helperText={L(
                        'You can use <b>bold</b>, <i>italic</i>, <ul><li>lists</li></ul>',
                        'Можно <b>жирный</b>, <i>курсив</i>, <ul><li>списки</li></ul>'
                      )}
                    />
                    {many && targetStudents.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        <Button
                          size="small"
                          onClick={() => setShowPerStudent((v) => !v)}
                          sx={{ pl: 0 }}
                        >
                          {showPerStudent ? '▾ ' : '▸ '}
                          {L(
                            'A personal comment per child',
                            'Свой комментарий каждому'
                          )}
                        </Button>
                        {showPerStudent && (
                          <Box sx={{ display: 'grid', gap: 1, mt: 0.5 }}>
                            {targetStudents.map((s) => (
                              <TextField
                                key={s._id}
                                fullWidth
                                size="small"
                                multiline
                                maxRows={4}
                                label={s.name}
                                value={perStudentComments[s._id] || ''}
                                onChange={(e) =>
                                  setPerStudentComments((prev) => ({
                                    ...prev,
                                    [s._id]: e.target.value,
                                  }))
                                }
                                placeholder={L(
                                  'Uses the common comment if empty',
                                  'Если пусто — общий комментарий'
                                )}
                                inputProps={{ maxLength: 2000 }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                )}

                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={notYet.enabled}
                      onChange={(e) =>
                        setNotYet({ ...notYet, enabled: e.target.checked })
                      }
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {L(
                        "Call very weak topics 'Not yet learned'",
                        'Слабые темы называть «Ещё не изучено»'
                      )}
                    </Typography>
                  }
                />
                {notYet.enabled && (
                  <Box sx={{ pl: 3, display: 'flex', flexDirection: 'column' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={notYet.exclude}
                          onChange={(e) =>
                            setNotYet({ ...notYet, exclude: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {L(
                            'Leave them out of the %',
                            'Не учитывать их в общем %'
                          )}
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={notYet.showAll}
                          onChange={(e) =>
                            setNotYet({ ...notYet, showAll: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {L(
                            'Still list every word',
                            'Всё равно показывать все слова'
                          )}
                        </Typography>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {L('Below', 'Ниже')}
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={notYet.threshold}
                        onChange={(e) =>
                          setNotYet({
                            ...notYet,
                            threshold: Math.max(
                              5,
                              Math.min(
                                50,
                                Math.round(Number(e.target.value) || 0)
                              )
                            ),
                          })
                        }
                        inputProps={{
                          min: 5,
                          max: 50,
                          step: 1,
                          style: { width: 56 },
                        }}
                      />
                      <Typography variant="body2">%</Typography>
                    </Box>
                  </Box>
                )}

                {many && groupOutput === 'zip' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      {L('In the ZIP', 'В архиве')}
                    </Typography>
                    {sectionCheck(
                      bundle,
                      setBundle,
                      'compact',
                      L(
                        'Also a 1-page print copy',
                        'Ещё версия для печати на 1 стр.'
                      )
                    )}
                    {sectionCheck(
                      bundle,
                      setBundle,
                      'cover',
                      L(
                        'Contents and summary sheet',
                        'Содержание и сводка по группе'
                      )
                    )}
                    {sectionCheck(
                      bundle,
                      setBundle,
                      'roster',
                      L(
                        'Summary table (CSV + JSON)',
                        'Сводная таблица (CSV + JSON)'
                      )
                    )}
                  </Box>
                )}

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, display: 'block', mt: 1.5 }}
                >
                  {L(
                    'Note at the end of each report',
                    'Примечание в конце отчёта'
                  )}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={notice.level}
                  onChange={(e, v) => v && setNotice({ ...notice, level: v })}
                  sx={{ mt: 0.5, flexWrap: 'wrap' }}
                >
                  <ToggleButton value="none">{L('None', 'Нет')}</ToggleButton>
                  <ToggleButton value="green" sx={{ color: '#16a34a' }}>
                    {L('Note', 'Заметка')}
                  </ToggleButton>
                  <ToggleButton value="yellow" sx={{ color: '#b45309' }}>
                    {L('Notice', 'Внимание')}
                  </ToggleButton>
                  <ToggleButton value="red" sx={{ color: '#dc2626' }}>
                    {L('Warning', 'Предупреждение')}
                  </ToggleButton>
                </ToggleButtonGroup>
                {notice.level !== 'none' && (
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    sx={{ mt: 1 }}
                    value={notice.text}
                    onChange={(e) =>
                      setNotice({ ...notice, text: e.target.value })
                    }
                    inputProps={{ maxLength: 600 }}
                  />
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ flex: '1 1 220px', minWidth: 0 }}
        >
          {ready
            ? `${files} · ${whoText || ''} · ${reportName}${report !== 'groupAnalytics' ? ` · ${periodLabel(reportPeriod)}` : ''} · ${lang.toUpperCase()}`
            : report === 'groupAnalytics'
              ? L('Choose groups', 'Выберите группы')
              : L('Choose who the report is for', 'Выберите, для кого отчёт')}
        </Typography>
        <Button onClick={onClose} disabled={loading}>
          {L('Cancel', 'Отмена')}
        </Button>
        <Button
          variant="contained"
          disabled={loading || !ready}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
          onClick={
            report === 'groupAnalytics' ? runGroupAnalytics : runCombined
          }
        >
          {L('Download', 'Скачать')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
